class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerDiscount extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('#CartDrawer-DiscountCode');
    this.applyButton = this.querySelector('#CartDrawer-DiscountApply');
    this.errorContainer = this.querySelector('#CartDrawer-DiscountError');
    this.appliedContainer = this.querySelector('#CartDrawer-AppliedDiscounts');

    this.bindEvents();
  }

  bindEvents() {
    this.applyButton?.addEventListener('click', this.applyDiscount.bind(this));

    // Stop change events from bubbling to cart-drawer-items (prevents quantity validation errors)
    this.input?.addEventListener('change', (e) => {
      e.stopPropagation();
    });

    this.input?.addEventListener('keypress', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        this.applyDiscount();
      }
    });

    this.querySelectorAll('.discount-tag__remove').forEach((btn) => {
      btn.addEventListener('click', this.removeDiscount.bind(this));
    });
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.classList.add('loading');
      this.applyButton.disabled = true;
    } else {
      this.classList.remove('loading');
      this.applyButton.disabled = false;
    }
  }

  showError(message) {
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
  }

  hideError() {
    this.errorContainer.textContent = '';
    this.errorContainer.hidden = true;
  }

  async applyDiscount() {
    const code = this.input?.value.trim();
    if (!code) return;

    this.setLoading(true);
    this.hideError();

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: code,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();
      this.handleResponse(cart, code);
    } catch (error) {
      console.error('Discount apply error:', error);
      this.showError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      this.setLoading(false);
    }
  }

  async removeDiscount(event) {
    const button = event.currentTarget;
    this.setLoading(true);
    this.hideError();

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: '',
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const cart = await response.json();
      this.refreshCartDrawer(cart.sections);
    } catch (error) {
      console.error('Discount remove error:', error);
      this.showError('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      this.setLoading(false);
    }
  }

  handleResponse(cart, code) {
    // Check if discount was applied by comparing with cart discounts
    // The discount code might appear in title or as the code itself
    const discountApplied = cart.cart_level_discount_applications?.some((d) => {
      const discountTitle = d.title || d.code || '';
      return discountTitle.toLowerCase() === code.toLowerCase();
    });

    // Also check if total_discount increased or if any discount exists after applying
    const hasDiscounts = cart.cart_level_discount_applications?.length > 0 || cart.total_discount > 0;

    if (discountApplied || hasDiscounts) {
      this.input.value = '';
      this.hideError();
      this.refreshCartDrawer(cart.sections);
    } else {
      this.showError('Rabattcode ungültig oder nicht anwendbar.');
    }
  }

  getSectionsToRender() {
    return [
      {
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  refreshCartDrawer(sections) {
    if (!sections) return;

    // Parse the new HTML
    const cartDrawerHTML = sections['cart-drawer'];
    if (cartDrawerHTML) {
      const parsedHTML = new DOMParser().parseFromString(cartDrawerHTML, 'text/html');

      // Replace cart-drawer-items element properly to remove old event listeners
      const oldCartItems = document.querySelector('cart-drawer-items');
      const newCartItems = parsedHTML.querySelector('cart-drawer-items');
      if (oldCartItems && newCartItems) {
        oldCartItems.replaceWith(newCartItems);
      }

      // Replace the discount section (now outside cart-drawer-items)
      const oldDiscount = document.querySelector('cart-drawer-discount');
      const newDiscount = parsedHTML.querySelector('cart-drawer-discount');
      if (oldDiscount && newDiscount) {
        oldDiscount.replaceWith(newDiscount);
      }

      // Update the footer separately
      const oldFooter = document.querySelector('.drawer__footer');
      const newFooter = parsedHTML.querySelector('.drawer__footer');
      if (oldFooter && newFooter) {
        oldFooter.replaceWith(newFooter);
      }
    }

    // Update cart icon bubble
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && sections['cart-icon-bubble']) {
      const parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newBubble = parsedBubble.querySelector('.shopify-section');
      if (newBubble) {
        cartIconBubble.innerHTML = newBubble.innerHTML;
      }
    }

    // Re-bind cart drawer overlay click
    const cartDrawer = document.querySelector('cart-drawer');
    const overlay = document.querySelector('#CartDrawer-Overlay');
    if (cartDrawer && overlay) {
      overlay.addEventListener('click', () => cartDrawer.close());
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-drawer-discount', CartDrawerDiscount);
